import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/ui/Button/Button';
import Flex from 'components/ui/Flex';
import Image from 'components/ui/Image';
import Input from 'components/ui/Input';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useOnClickOutside from 'hooks/useCloseOnClickOutside';
import debounce from 'lodash.debounce';
import { useEffect, useRef, useState } from 'react';
import { ImageExtension, ShopListProduct } from 'microshop-api';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { searchPhraseSet, searchProducts, selectCategories } from 'store/reducers/assortmentSlice';
import styled from 'styled-components';
import getImage, { ImageType } from 'utils/getImage';
import Spinner from './Spinner';

interface SearchResultItemProps {
    selectedCategory: string | null | undefined;
    product: ShopListProduct;
    onItemClick: () => void;
}

const SearchInput = ({
    onSearch,
    onKeyDown,
    inputRef,
    containerRef,
}: {
    onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    containerRef: React.RefObject<HTMLDivElement>;
}) => {
    const { t } = useTranslation();

    return (
        <InputWrapper ref={containerRef} className="w-100 mx-3">
            <Input
                placeholder={t('search')}
                ref={inputRef}
                onChange={onSearch}
                onKeyDown={onKeyDown}
                className="w-100"
                name="Search"
                icon={faSearch}
                rounded
            />
        </InputWrapper>
    );
};

const SearchResultItem = ({ selectedCategory, product, onItemClick }: SearchResultItemProps) => {
    const link = selectedCategory ? `/product/${selectedCategory}/${product.slug}` : `/product/${product.slug}`;
    return (
        <Flex column key={product.productNumber || product.productName} onClick={onItemClick}>
            <Link to={link}>
                <ResultItemContainer align="center" $gap={12}>
                    <ImageContainer className="mr-2 p-1">
                        <StyledImage
                            $small={false}
                            src={getImage(product.image, ImageType.ThumbNail, ImageExtension.Jpg)}
                            alt={`${product.productBrandName || ''} ${product.productNumber || ''}`}
                        />
                    </ImageContainer>
                    <ProductInfo column>
                        <Text small fontSize={14} textOverflow="225" className="f1-500">
                            {product.productName}
                        </Text>
                        <Text small color="textLight" className="text-nowrap">
                            {product.productNumber}
                        </Text>
                        <Text small color="textLight" className="text-nowrap">
                            {product.productBrandName}
                        </Text>
                    </ProductInfo>
                </ResultItemContainer>
            </Link>
        </Flex>
    );
};

const SearchResultsDropdownComponent = ({
    results,
    totalItems,
    loading,
    onItemClick,
    onShowAllResults,
    containerRef,
    inputContainerRef,
}: {
    results: ShopListProduct[];
    totalItems: number | undefined;
    loading: boolean;
    onItemClick: () => void;
    onShowAllResults: () => void;
    containerRef: React.RefObject<HTMLDivElement>;
    inputContainerRef: React.RefObject<HTMLDivElement>;
}) => {
    const { t } = useTranslation();
    const categories = useAppSelector(selectCategories);
    return (
        <SearchResultsDropdown
            ref={containerRef}
            $left={inputContainerRef.current?.offsetLeft || 0}
            $width={inputContainerRef.current?.offsetWidth || '100%'}
        >
            {loading ? (
                <Spinner />
            ) : !results?.length ? (
                <NoResults>{t('noMatches')}</NoResults>
            ) : (
                <>
                    <SearchResultsList>
                        {results.map((product) => {
                            const selectedCategoryKey = Object.keys(categories)?.find(
                                (key) => categories[key].code === product.nodeId,
                            );
                            const selectedCategory = selectedCategoryKey
                                ? categories[selectedCategoryKey].slug
                                : undefined;

                            return (
                                <SearchResultItem
                                    key={product.productNumber || product.productName}
                                    selectedCategory={selectedCategory}
                                    product={product}
                                    onItemClick={onItemClick}
                                />
                            );
                        })}
                    </SearchResultsList>
                    <ShowAllButton pill bgColor="fillWhite" onClick={onShowAllResults}>
                        {t('products_all')} {`(${totalItems})`}
                    </ShowAllButton>
                </>
            )}
        </SearchResultsDropdown>
    );
};

export const Search = ({ autoFocus, closeSearch }: { autoFocus?: boolean; closeSearch?: () => void }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const inputContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [search, setSearch] = useState('');

    const searchResults = useAppSelector((state) => state.assortment.searchResults);
    const { results, totalItems, loading } = searchResults;

    const showAllResults = () => {
        navigate('/search');
        dispatch(searchPhraseSet(search));
        clearSearch();
        closeSearch?.();
    };

    const enterClick = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            showAllResults();
        }
    };

    const handleSearch = (searchPhrase: string) => {
        if (!searchPhrase) return;

        dispatch(
            searchProducts({
                search: searchPhrase,
            }),
        );
    };

    useEffect(() => {
        setTimeout(() => {
            if (autoFocus && inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, [autoFocus]);

    const clearSearch = () => {
        setSearch('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const resultsContainerRef = useOnClickOutside(() => {
        clearSearch();
    }, !!search);

    const debouncedSearch = debounce((e) => {
        setSearch(e.target.value);
        handleSearch(e.target.value);
    }, 500);

    const shouldShowResults = !!search;

    return (
        <SearchContainer>
            <SearchInput
                onSearch={debouncedSearch}
                onKeyDown={enterClick}
                inputRef={inputRef}
                containerRef={inputContainerRef}
            />

            {shouldShowResults && (
                <SearchResultsDropdownComponent
                    results={results}
                    totalItems={totalItems}
                    loading={loading}
                    onItemClick={clearSearch}
                    onShowAllResults={showAllResults}
                    containerRef={resultsContainerRef}
                    inputContainerRef={inputContainerRef}
                />
            )}
        </SearchContainer>
    );
};

const SearchContainer = styled(Flex)`
    width: 100%;
    position: relative;
`;

const InputWrapper = styled.div`
    width: 100%;
`;

const SearchResultsDropdown = styled.div<{ $left: number; $width: number | string }>`
    position: absolute;
    top: 100%;
    left: ${(props) => props.$left}px;
    width: ${(props) => (typeof props.$width === 'number' ? `${props.$width}px` : props.$width)};
    background: #f4f4f4;
    color: #000000;
    padding: 12px;
    z-index: 10;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    margin-top: 4px;
`;

const SearchResultsList = styled.div``;

const ProductInfo = styled(Flex)`
    flex-grow: 2;
    font-size: 12px;
`;
const ResultItemContainer = styled(Flex)`
    padding: 8px;
    background: #ffffff;
    margin-bottom: 4px;
    transition: background-color 0.2s;

    &:hover {
        ${ProductInfo} ${Text}:first-child {
            text-decoration: underline;
        }
    }
`;

const ImageContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledImage = styled(Image)<{ $small?: boolean }>`
    width: 55px;
    max-height: 65px;
    object-fit: contain;

    ${({ $small }) =>
        $small &&
        `
      max-height: 35px;
      width: 35px;
    `}
`;

const NoResults = styled.p`
    margin: 8px 0;
`;

const ShowAllButton = styled(Button)`
    width: 100%;
    margin-top: 8px;
    border: 1px solid;
    border-color: ${({ theme }) => theme.colors.main};
`;
